import React, { useState } from 'react';
import Button from '../../../components/base/Button';

export default function FundraisingSection() {
  const [selectedAmount, setSelectedAmount] = useState('10000');
  const [customAmount, setCustomAmount] = useState('');
  const [donorInfo, setDonorInfo] = useState({
    name: '',
    email: '',
    phone: '',
    pan: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const donationAmounts = [
    { amount: '5000', impact: 'Supports 1 family with basic screening' },
    { amount: '10000', impact: 'Sponsors 2 families with complete care' },
    { amount: '25000', impact: 'Covers 5 families with app, OxyKits & education' },
    { amount: '50000', impact: 'Supports 10 families with comprehensive program' }
  ];

  const handleDonationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!donorInfo.name || !donorInfo.email) return;

    setIsSubmitting(true);
    try {
      const formDataToSubmit = new URLSearchParams();
      formDataToSubmit.append('name', donorInfo.name);
      formDataToSubmit.append('email', donorInfo.email);
      formDataToSubmit.append('phone', donorInfo.phone);
      formDataToSubmit.append('pan', donorInfo.pan);
      formDataToSubmit.append('amount', customAmount || selectedAmount);
      formDataToSubmit.append('type', 'individual_donation');

      await fetch('https://readdy.ai/api/form/d30mc3iuu7nqqcqu7tdg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSubmit.toString(),
      });

      setIsSubmitted(true);
      setDonorInfo({ name: '', email: '', phone: '', pan: '' });
      setCustomAmount('');
    } catch (error) {
      console.error('Donation form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-6">
            Your Support Delivers Oxygen
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed mb-8">
            Every donation funds oxygen-first solutions for families in need. 
            Transparent, auditable, 80G-eligible contributions.
          </p>
          
          {/* Impact Statement */}
          <div className="bg-[#12B6B6]/10 rounded-2xl p-8">
            <div className="text-3xl font-bold text-[#12B6B6] mb-2">₹10,000</div>
            <p className="text-lg text-[#111111] font-semibold mb-2">
              Will provide app access, OxyKits (capsules + sprays), and oxygen education for 2 families for a full year
            </p>
            <p className="text-gray-600">
              Complete oxygen care package • Educational materials • Follow-up support
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Individual Donations */}
          <div className="bg-[#FAFAFA] rounded-3xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#12B6B6]/10 rounded-2xl flex items-center justify-center">
                <i className="ri-heart-3-line text-3xl text-[#12B6B6]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-2">Support a Family Today</h3>
              <p className="text-gray-600">Make a direct impact on families in need</p>
            </div>

            {isSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                  <i className="ri-check-line text-3xl text-green-600"></i>
                </div>
                <h4 className="text-xl font-bold text-[#111111] mb-2">Thank You!</h4>
                <p className="text-gray-600">Your donation will help save lives. Instant 80G receipt via email.</p>
              </div>
            ) : (
              <>
                {/* Amount Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#111111] mb-3">Select Amount</label>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {donationAmounts.map((donation) => (
                      <button
                        key={donation.amount}
                        type="button"
                        onClick={() => {
                          setSelectedAmount(donation.amount);
                          setCustomAmount('');
                        }}
                        className={`p-3 text-left rounded-xl border-2 transition-all duration-200 whitespace-nowrap cursor-pointer ${
                          selectedAmount === donation.amount && !customAmount
                            ? 'border-[#12B6B6] bg-[#12B6B6]/5'
                            : 'border-gray-200 hover:border-[#12B6B6]/50'
                        }`}
                      >
                        <div className="font-bold text-[#111111]">₹{donation.amount}</div>
                        <div className="text-xs text-gray-600">{donation.impact}</div>
                      </button>
                    ))}
                  </div>
                  <input
                    type="number"
                    placeholder="Custom amount"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setSelectedAmount('');
                    }}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                  />
                </div>

                {/* Donor Form */}
                <form onSubmit={handleDonationSubmit} id="donation-form" data-readdy-form className="space-y-4">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Full Name *"
                      value={donorInfo.name}
                      onChange={(e) => setDonorInfo({...donorInfo, name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address *"
                      value={donorInfo.email}
                      onChange={(e) => setDonorInfo({...donorInfo, email: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      value={donorInfo.phone}
                      onChange={(e) => setDonorInfo({...donorInfo, phone: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="pan"
                      placeholder="PAN Number (for 80G receipt)"
                      value={donorInfo.pan}
                      onChange={(e) => setDonorInfo({...donorInfo, pan: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    variant="primary" 
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Processing...' : `Donate Now ₹${customAmount || selectedAmount}`}
                  </Button>
                </form>
              </>
            )}

            <div className="mt-6 text-center">
              <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
                <div className="flex items-center space-x-1">
                  <i className="ri-shield-check-line w-4 h-4 flex items-center justify-center text-green-600"></i>
                  <span>Instant 80G receipt</span>
                </div>
                <div className="flex items-center space-x-1">
                  <i className="ri-smartphone-line w-4 h-4 flex items-center justify-center text-blue-600"></i>
                  <span>UPI, Card, Netbanking</span>
                </div>
              </div>
            </div>
          </div>

          {/* CSR Partnerships */}
          <div className="bg-[#12B6B6]/5 rounded-3xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#12B6B6]/10 rounded-2xl flex items-center justify-center">
                <i className="ri-building-line text-3xl text-[#12B6B6]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-2">CSR That Prevents Disease Before It Begins</h3>
              <p className="text-gray-600">Corporate partnerships for district-wide impact</p>
            </div>

            <div className="space-y-6">
              {/* CSR Benefits */}
              <div className="space-y-4">
                <h4 className="font-semibold text-[#111111]">Corporates can sponsor district pilots with:</h4>
                <div className="space-y-3">
                  {[
                    'Branded visibility across all program materials',
                    'Employee engagement through volunteer opportunities',
                    'Quarterly audited impact reports',
                    'Direct community impact measurement',
                    'Tax benefits under CSR compliance'
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-[#12B6B6] mt-0.5"></i>
                      <span className="text-sm text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CSR Programs */}
              <div className="bg-white rounded-2xl p-6">
                <h4 className="font-semibold text-[#111111] mb-4">District Pilot Programs:</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">Community Screening Initiative</span>
                    <span className="text-sm font-medium text-[#12B6B6]">₹10L+</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">OxyKit Distribution Program</span>
                    <span className="text-sm font-medium text-[#12B6B6]">₹25L+</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">Full District Implementation</span>
                    <span className="text-sm font-medium text-[#12B6B6]">₹50L+</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Button variant="primary" className="w-full">
                  Partner with Us
                </Button>
                <Button variant="outline" className="w-full">
                  Download CSR Deck
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
